import React from "react"
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { useUpdateApplicationStatus } from "@/api/applicationQueries"
import { toast } from "react-hot-toast"

interface BulkDeclineModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    selectedApplicationIds: string[]
    onSuccess?: () => void
}

export function BulkDeclineModal({
    open,
    onOpenChange,
    selectedApplicationIds,
    onSuccess,
}: BulkDeclineModalProps) {
    const [declineReason, setDeclineReason] = React.useState("")
    const updateApplicationStatus = useUpdateApplicationStatus("")

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!declineReason.trim()) {
            toast.error("Please provide a decline reason")
            return
        }

        try {
            await updateApplicationStatus.mutateAsync({
                reviewStatus: "declined",
                declineReason: declineReason.trim(),
                applicationIds: selectedApplicationIds,
            })

            toast.success(
                `Successfully declined ${selectedApplicationIds.length} applications`
            )
            onSuccess?.()
            onOpenChange(false)
        } catch (error) {
            toast.error("Failed to decline applications. Please try again.")
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Decline Applications</DialogTitle>
                    <DialogDescription>
                        You are about to decline {selectedApplicationIds.length}{" "}
                        applications. This action cannot be undone.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <Textarea
                            placeholder="Enter reason for declining these applications..."
                            value={declineReason}
                            onChange={(e) => setDeclineReason(e.target.value)}
                            className="min-h-[100px]"
                            disabled={updateApplicationStatus.isPending}
                        />
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            disabled={updateApplicationStatus.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="destructive"
                            disabled={updateApplicationStatus.isPending}
                        >
                            {updateApplicationStatus.isPending
                                ? "Declining..."
                                : "Decline Applications"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
